/**
 * Transfer drawer — mirrors KC2 transfer sidebar.
 * Fields: Team (required), Agent (optional, filtered by team, assigned agent disabled),
 * Category, Sub Category, Quick Reply, Notes, Privacy toggle, CC emails,
 * Description (required, rich text editor), Attachments.
 * POST /conversations/:id/transfer via useConversationActions.transfer.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import { ArrowLeftRight, FileText, Lock, LockOpen, Paperclip, Users, X } from 'lucide-react'
import { useAssignableAgents, formatLoad } from './useAssignableAgents.js'
import { useCategories } from '../../../hooks/useCategories.js'
import { useSubcategories } from '../../../hooks/useSubcategories.js'
import { useConversationQuickReplies } from '../../../hooks/useConversationManagement.js'
import { useUploadAttachment } from '../../../hooks/useUploadAttachment.js'
import RichTextEditor, { isEmptyHtml } from '../../common/RichTextEditor.js'
import ActionDrawer from './ActionDrawer.js'
import { Field, GhostButton, PrimaryButton, SelectField, htmlToText, inputClass, textareaClass } from './primitives.js'
import type { UseConversationActionsReturn } from './useConversationActions.js'

interface PendingAttachment {
  localId: string
  name: string
  state: 'uploading' | 'uploaded' | 'error'
  attachmentId?: string | undefined
  mimeType?: string | undefined
  previewUrl?: string | undefined
  file?: File | undefined
}

export interface TransferDrawerProps {
  open: boolean
  onClose: () => void
  actions: UseConversationActionsReturn
  /** Currently assigned agent's id — disabled in the agent dropdown. */
  currentAgentId: string | null
}

export default function TransferDrawer({ open, onClose, actions, currentAgentId }: TransferDrawerProps): JSX.Element {
  const { agents, teams } = useAssignableAgents()
  const { categories } = useCategories(true)
  const [categoryId, setCategoryId] = useState('')
  const [subcategoryId, setSubcategoryId] = useState('')
  const { subcategories } = useSubcategories(categoryId || undefined)
  const { items: quickReplies } = useConversationQuickReplies()
  const { upload } = useUploadAttachment()

  const [teamId, setTeamId] = useState('')
  const [agentId, setAgentId] = useState('')
  const [description, setDescription] = useState('')
  const [notes, setNotes] = useState('')
  const [isPrivate, setIsPrivate] = useState(true)
  const [ccInput, setCcInput] = useState('')
  const [ccList, setCcList] = useState<string[]>([])
  const [pending, setPending] = useState<PendingAttachment[]>([])
  const [attachmentIds, setAttachmentIds] = useState<string[]>([])
  const fileRef = useRef<HTMLInputElement>(null)

  // Reset state when drawer opens
  useEffect(() => {
    if (open) {
      setTeamId('')
      setAgentId('')
      setDescription('')
      setNotes('')
      setCategoryId('')
      setSubcategoryId('')
      setIsPrivate(true)
      setCcList([])
      setCcInput('')
      setPending([])
      setAttachmentIds([])
    }
  }, [open])

  const teamOptions = useMemo(
    () => teams.map((t) => ({ value: t.id, label: t.name })),
    [teams],
  )

  // Agents filtered by selected team via team_members; show all agents for that team.
  // Disable the currently assigned agent (mirrors KC2). Each label carries the
  // agent's conversation load so a supervisor can see who is already saturated —
  // agents at their cap are still listed and selectable, since transferring is
  // a deliberate act, but they're marked so it isn't done by accident.
  const agentOptions = useMemo(() => {
    return agents
      .filter((a) => !teamId || a.teamIds.includes(teamId))
      .map((a) => ({
        value: a.agentId,
        label: `${a.label} · ${formatLoad(a)}${a.atCapacity ? ' (full)' : ''}`,
        disabled: a.agentId === currentAgentId,
      }))
  }, [agents, teamId, currentAgentId])

  const categoryOptions = useMemo(
    () => categories.map((c) => ({ value: c.id, label: c.name })),
    [categories],
  )

  const subcategoryOptions = useMemo(
    () => subcategories.map((s) => ({ value: s.id, label: s.name })),
    [subcategories],
  )

  const quickReplyOptions = useMemo(
    () => quickReplies.map((q) => ({ value: q.id, label: q.name })),
    [quickReplies],
  )

  const canSubmit = Boolean(teamId && !isEmptyHtml(description))

  const addCc = (): void => {
    const email = ccInput.trim()
    if (email && !ccList.includes(email)) {
      setCcList([...ccList, email])
      setCcInput('')
    }
  }

  const removeCc = (email: string): void => {
    setCcList(ccList.filter((e) => e !== email))
  }

  const onQuickReplyChange = (id: string): void => {
    const qr = quickReplies.find((q) => q.id === id)
    if (qr) setDescription(qr.reply)
  }

  /* ── Attachments ── */
  const addFiles = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return
      const list = Array.from(files)
      const additions: PendingAttachment[] = list.map((f) => ({
        localId: crypto.randomUUID(),
        name: f.name,
        state: 'uploading' as const,
        mimeType: f.type || 'application/octet-stream',
        file: f,
      }))
      setPending((prev) => [...prev, ...additions])
      additions.forEach((a, i) => {
        const file = list[i]
        if (!file) return
        void (async () => {
          try {
            const res = await upload(file)
            const isImage = res.mimeType.startsWith('image/')
            setPending((prev) => prev.map((p) => p.localId === a.localId ? {
              ...p,
              state: 'uploaded' as const,
              attachmentId: res.attachmentId,
              mimeType: res.mimeType,
              previewUrl: isImage ? `/api/attachments/${res.attachmentId}` : undefined,
            } : p))
            setAttachmentIds((prev) => [...prev, res.attachmentId])
          } catch {
            setPending((prev) => prev.map((p) => p.localId === a.localId ? { ...p, state: 'error' as const } : p))
          }
        })()
      })
    },
    [upload],
  )

  const removeAttachment = (localId: string): void => {
    setPending((prev) => {
      const removed = prev.find((p) => p.localId === localId)
      if (removed?.attachmentId) setAttachmentIds((ids) => ids.filter((id) => id !== removed.attachmentId))
      return prev.filter((p) => p.localId !== localId)
    })
  }

  const uploading = pending.some((p) => p.state === 'uploading')

  const submit = async (): Promise<void> => {
    const ok = await actions.transfer({
      transferType: 'cold',
      toTeamId: teamId,
      ...(agentId ? { toAgentId: agentId } : {}),
      description: htmlToText(description),
      isPrivate,
      ...(ccList.length > 0 ? { cc: ccList } : {}),
      ...(categoryId ? { reason: `Category: ${categories.find(c => c.id === categoryId)?.name ?? ''}${subcategoryId ? ` / ${subcategories.find(s => s.id === subcategoryId)?.name ?? ''}` : ''}` } : {}),
      ...(attachmentIds.length > 0 ? { attachments: attachmentIds } : {}),
    })
    if (!ok) return
    // The transfer payload only carries a reason string — the reclassification
    // itself goes through the conversation PATCH so the row actually lands in
    // the right bucket for the receiving team.
    if (categoryId || subcategoryId) {
      const patch: Record<string, string> = {}
      if (categoryId) patch.categoryId = categoryId
      if (subcategoryId) patch.subcategoryId = subcategoryId
      await actions.updateProps(patch, 'Conversation reclassified')
    }
    onClose()
  }

  return (
    <ActionDrawer
      open={open}
      onClose={onClose}
      title="Transfer"
      subtitle="Reassign this conversation"
      icon={<ArrowLeftRight className="h-4 w-4" />}
      info={{
        lead: 'Hands this conversation to another team or agent, with a note explaining why.',
        points: [
          { term: 'Team', detail: 'Required — where the conversation lands. Leave the agent blank to let the team pick it up from their queue.' },
          { term: 'Agent', detail: 'Optional. The current assignee is listed but disabled; each name shows their open load.' },
          { term: 'Category / Sub Category', detail: 'Reclassify while transferring, so the receiving team gets it filed correctly.' },
          { term: 'Description', detail: 'Required — the handover note. Private keeps it agent-only; public is visible to the requester.' },
          { term: 'CC', detail: 'Extra addresses that should receive the transfer notification.' },
        ],
        caution: 'The conversation leaves your queue as soon as you transfer it.',
      }}
      width={760}
      footer={
        <>
          <GhostButton onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton onClick={() => void submit()} disabled={!canSubmit || actions.busy || uploading}>
            {actions.busy ? 'Transferring…' : 'Submit'}
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-4">
        {/* Team (required) */}
        <Field label="Team" required>
          <SelectField
            icon={<Users className="h-4 w-4" />}
            value={teamId}
            onChange={(v) => {
              setTeamId(v)
              setAgentId('')
            }}
            placeholder="Select Team"
            options={teamOptions}
          />
        </Field>

        {/* Agent (optional, filtered by team, assigned agent disabled) */}
        <Field label="Agent">
          <SelectField
            value={agentId}
            onChange={setAgentId}
            placeholder={teamId ? 'Select Agent' : 'Select a team first'}
            options={agentOptions}
            disabled={!teamId}
          />
        </Field>

        {/* CC emails */}
        <Field label="CC" hint="Press 'Enter' after entering each email address">
          <div className="flex gap-2">
            <input
              type="email"
              value={ccInput}
              onChange={(e) => setCcInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addCc()
                }
              }}
              placeholder="Enter email and press Enter"
              className={`${inputClass} flex-1`}
            />
            <button
              type="button"
              onClick={addCc}
              className="shrink-0 rounded-[3px] bg-[#3366FF] px-3 py-2 text-xs font-medium text-white hover:bg-[#2855dd]"
            >
              Add
            </button>
          </div>
          {ccList.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {ccList.map((email) => (
                <span
                  key={email}
                  className="inline-flex items-center gap-1 rounded-[3px] bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600"
                >
                  {email}
                  <button
                    type="button"
                    onClick={() => removeCc(email)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </Field>

        {/* Category */}
        <Field label="Category">
          <SelectField
            value={categoryId}
            onChange={(v) => {
              setCategoryId(v)
              setSubcategoryId('')
            }}
            placeholder="Select Category"
            options={categoryOptions}
          />
        </Field>

        {/* Sub Category */}
        <Field label="Sub Category">
          <SelectField
            value={subcategoryId}
            onChange={setSubcategoryId}
            placeholder={categoryId ? 'Select Sub Category' : 'Select a category first'}
            options={subcategoryOptions}
            disabled={!categoryId}
          />
        </Field>

        {/* Quick Reply */}
        <Field label="Quick Reply">
          <SelectField
            value=""
            onChange={onQuickReplyChange}
            placeholder="Select Quick Reply"
            options={quickReplyOptions}
          />
        </Field>

        {/* Notes */}
        <Field label="Notes">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Add Notes here...."
            className={textareaClass}
          />
        </Field>

        {/* Privacy toggle */}
        <Field label="Privacy">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsPrivate(!isPrivate)}
              className={`flex items-center gap-1.5 rounded-[3px] border px-3 py-1.5 text-xs font-medium transition-colors ${
                isPrivate
                  ? 'border-[#3366FF] bg-blue-50 text-[#3366FF]'
                  : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
              }`}
            >
              {isPrivate ? <Lock className="h-3.5 w-3.5" /> : <LockOpen className="h-3.5 w-3.5" />}
              {isPrivate ? 'Private' : 'Public'}
            </button>
          </div>
        </Field>

        {/* Description (required, rich text editor) */}
        <Field label="Description" required>
          <RichTextEditor
            value={description}
            onChange={setDescription}
            height={150}
            placeholder="Enter description…"
          />
        </Field>

        {/* Attachments */}
        <Field label="Attachments">
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault() }}
            onDrop={(e) => {
              e.preventDefault()
              addFiles(e.dataTransfer.files)
            }}
            className="cursor-pointer rounded-[3px] border-2 border-dashed border-slate-200 px-4 py-6 text-center text-xs text-slate-400 hover:border-slate-300 hover:bg-slate-50"
          >
            <Paperclip className="mx-auto mb-2 h-5 w-5 text-slate-300" />
            Drag 'n' drop some files here, or click to select files.
          </div>
          <input
            ref={fileRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => addFiles(e.target.files)}
          />
          {pending.length > 0 && (
            <div className="mt-2 space-y-1.5">
              {pending.map((p) => (
                <div key={p.localId} className="flex items-center gap-2 rounded-[3px] border border-slate-100 px-3 py-2">
                  {p.previewUrl ? (
                    <img src={p.previewUrl} alt={p.name} className="h-8 w-8 rounded object-cover" />
                  ) : (
                    <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                  )}
                  <span className="flex-1 truncate text-xs text-slate-600">{p.name}</span>
                  {p.state === 'uploading' && (
                    <span className="text-xs text-slate-400">Uploading…</span>
                  )}
                  {p.state === 'error' && (
                    <span className="text-xs text-red-500">Failed</span>
                  )}
                  <button
                    type="button"
                    onClick={() => removeAttachment(p.localId)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </Field>
      </div>
    </ActionDrawer>
  )
}
